/* What a hover reveal needs to know is which pointer is in use RIGHT NOW, and
   only an event can say that. Media queries describe the device: (hover: hover)
   asks about the primary pointer and hides the reveal from a touchscreen
   laptop's mouse, and (any-hover: hover) lets an iPad's finger tap trip it --
   iOS applies :hover on the first tap, the reveal changes the page under the
   finger, and Safari withholds the click until a second tap.

   So the last pointer to move marks <html>, and a reveal reads the mark. Same
   idea as the pointerType check in useIsHoverFocused, for CSS. */

const CAN_HOVER = "(any-hover: hover)";

/** Some pointer on this device is a finger. Gate a tap fallback for a hover reveal on this. */
export const CAN_TOUCH = "(any-pointer: coarse)";

/**
 * Put this in front of a hover reveal's selector: `${NOT_TOUCH} tr:hover &`.
 * Off only while a finger is in use, so a page that never marked (Storybook,
 * tests) hovers like it always did. :where() so it adds no specificity -- a
 * reveal's hover rule must not start beating whatever it lost to before.
 */
export const NOT_TOUCH = ':where(:root:not([data-pointer="touch"]))';

/**
 * Starts marking <html> with the pointer in use. Called once, before the app
 * renders (index.js). A finger counts from its pointerover, the first thing a
 * tap sends, ahead of the :hover iOS applies for it. A mouse only counts once
 * it moves or presses -- when a tap ends Chrome sends a mouse pointerover for
 * whatever the resting cursor is over, and that flipped the mark straight back.
 */
export const markPointerType = () => {
  const root = document.documentElement;
  const set = (type) => {
    if (root.getAttribute("data-pointer") !== type)
      root.setAttribute("data-pointer", type);
  };

  if (!window.matchMedia?.(CAN_HOVER)?.matches) set("touch");

  const onPointer = (e) => {
    if (e.pointerType === "touch") set("touch");
    else if (e.pointerType && e.type !== "pointerover") set("mouse");
  };
  const types = ["pointerover", "pointerdown", "pointermove"];
  types.forEach((type) =>
    document.addEventListener(type, onPointer, { capture: true, passive: true })
  );
  return () =>
    types.forEach((type) =>
      document.removeEventListener(type, onPointer, { capture: true })
    );
};
